import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Typography,
  App,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  supplierInvoiceService,
  type SupplierInvoiceDTO,
  type SupplierInvoiceFilters,
} from '@/services/supplierInvoice.service';
import { sumSupplierInvoiceOverheadAmount } from '@/utils/supplierInvoiceOverhead';
import { useAiListSelection } from '@/hooks/useAiListSelection';

const { Title, Text } = Typography;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

export const SupplierInvoicesPage: React.FC = () => {
  const { t } = useTranslation('procurement');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [supplierFilter, setSupplierFilter] = useState<string | undefined>(undefined);
  const [dateFrom, setDateFrom] = useState<string | undefined>(undefined);
  const [dateTo, setDateTo] = useState<string | undefined>(undefined);

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      Draft: t('statusDraft'),
      Submitted: t('statusSubmitted'),
      Approved: t('statusApproved'),
      Posted: t('statusPosted'),
      Cancelled: t('statusCancelled'),
    };
    return map[s] ?? s;
  };

  const filters: SupplierInvoiceFilters = {
    bill_number: searchText || undefined,
    status: statusFilter,
    supplier_id: supplierFilter,
    date_from: dateFrom,
    date_to: dateTo,
  };

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['supplier-invoices', filters],
    queryFn: () => supplierInvoiceService.list(filters),
  });
  const { rowSelection } = useAiListSelection({
    entityType: 'SupplierInvoice',
    module: 'procurement',
    listFilters: filters as Record<string, unknown>,
    labelById: (id) => list.find((o) => o.id === id)?.invoice_number,
  });

  const handleSubmit = async (id: string) => {
    try {
      await supplierInvoiceService.submit(id);
      message.success(t('invoiceSubmitted'));
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('failedToSubmit'));
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await supplierInvoiceService.approve(id);
      message.success(t('invoiceApproved'));
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('failedToApprove'));
    }
  };

  const handlePost = async (id: string) => {
    try {
      await supplierInvoiceService.post(id);
      message.success(t('invoicePostedToGl'));
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('failedToPost'));
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await supplierInvoiceService.cancel(id);
      message.success(t('invoiceCancelled'));
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('failedToCancel'));
    }
  };

  const columns: ColumnsType<SupplierInvoiceDTO> = [
    {
      title: t('billNo'),
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      fixed: 'left',
      width: 130,
      render: (val: string, record: SupplierInvoiceDTO) => (
        <a onClick={() => navigate(`/scm/procurement/supplier-invoices/${record.id}`)}>{val ?? '—'}</a>
      ),
    },
    {
      title: t('supplierInvoiceNo'),
      dataIndex: 'supplier_invoice_number',
      key: 'supplier_invoice_number',
      width: 140,
    },
    {
      title: t('date'),
      dataIndex: 'received_date',
      key: 'received_date',
      width: 110,
      render: (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: t('supplier'),
      key: 'supplier',
      width: 160,
      render: (_, r) => r.supplier?.supplier_name ?? r.supplier_id ?? '—',
    },
    {
      title: t('overheads'),
      key: 'overhead_amount',
      width: 120,
      align: 'right',
      render: (_, r) => {
        const oh = sumSupplierInvoiceOverheadAmount(r);
        if (oh <= 0) return '—';
        return (
          <Space size={4}>
            <Tag color="orange">{t('overheadShort')}</Tag>
            <span>{oh.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </Space>
        );
      },
    },
    {
      title: t('grandTotal'),
      dataIndex: 'grand_total',
      key: 'grand_total',
      width: 110,
      align: 'right',
      render: (v) => (v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'),
    },
    {
      title: tc('status'),
      dataIndex: 'invoice_status',
      key: 'invoice_status',
      width: 100,
      render: (s: string) => <Tag color={statusColors[s] ?? 'default'}>{statusLabel(s)}</Tag>,
    },
    {
      title: tc('actions'),
      key: 'actions',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/scm/procurement/supplier-invoices/${record.id}`)}>
            {tc('view')}
          </Button>
          {['Draft', 'Submitted'].includes(record.invoice_status) && (
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => navigate(`/scm/procurement/supplier-invoices/${record.id}/edit`)}>
              {tc('edit')}
            </Button>
          )}
          {record.invoice_status === 'Draft' && (
            <Button type="link" size="small" onClick={() => handleSubmit(record.id)}>{tc('submit')}</Button>
          )}
          {record.invoice_status === 'Submitted' && (
            <Button type="link" size="small" onClick={() => handleApprove(record.id)}>{tc('approve')}</Button>
          )}
          {record.invoice_status === 'Approved' && (
            <Button type="link" size="small" onClick={() => handlePost(record.id)}>{tc('post')}</Button>
          )}
          {!['Posted', 'Cancelled'].includes(record.invoice_status) && (
            <Button type="link" size="small" danger onClick={() => handleCancel(record.id)}>{tc('cancel')}</Button>
          )}
          <Button type="link" size="small" icon={<PrinterOutlined />} onClick={() => navigate(`/scm/procurement/supplier-invoices/${record.id}`)}>
            {t('print')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Title level={4} style={{ margin: 0 }}>
            {t('supplierInvoicesTitle')}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            {t('supplierInvoicesSubtitle')}
          </Text>
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16 }}
            message={t('purchaseBillRegister')}
            description={t('purchaseBillRegisterHint')}
          />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/supplier-invoices/new')}>
            {t('createPurchaseBill')}
          </Button>
        </Col>
      </Row>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder={t('searchSupplierInvoices')}
                allowClear
                onPressEnter={(e) => setSearchText((e.target as HTMLInputElement).value)}
                onChange={(e) => setSearchText(e.target.value)}
                value={searchText}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder={tc('status')}
                allowClear
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'Draft', label: t('statusDraft') },
                  { value: 'Submitted', label: t('statusSubmitted') },
                  { value: 'Approved', label: t('statusApproved') },
                  { value: 'Posted', label: t('statusPosted') },
                  { value: 'Cancelled', label: t('statusCancelled') },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Input
                type="date"
                placeholder={t('fromDate')}
                value={dateFrom ?? ''}
                onChange={(e) => setDateFrom(e.target.value || undefined)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Input
                type="date"
                placeholder={t('toDate')}
                value={dateTo ?? ''}
                onChange={(e) => setDateTo(e.target.value || undefined)}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>

          <Table
            loading={isLoading}
            columns={columns}
            dataSource={list.map((o) => ({ ...o, key: o.id }))}
            rowKey="id"
            rowSelection={rowSelection}
            sticky={{ offsetHeader: 0 }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (count) => t('totalBills', { count }),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            size="small"
            scroll={{ x: 1220, y: 520 }}
          />
        </Space>
      </Card>
    </div>
  );
};
